import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  FileText,
  Upload,
  Download,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGuides = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_url: '',
    cover_image: '',
    published: true
  });

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/guides`);
      setGuides(response.data.guides || []);
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setMessage({ type: 'error', text: 'Doar fișiere PDF sunt acceptate!' });
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload/pdf`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      if (response.data.success) {
        let fileUrl = response.data.url;
        if (fileUrl.startsWith('/')) {
          fileUrl = `${API_URL}${fileUrl}`;
        }
        setFormData(prev => ({
          ...prev,
          file_url: fileUrl
        }));
        setMessage({ type: 'success', text: `PDF "${response.data.original_name}" încărcat cu succes!` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la încărcarea PDF-ului' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!formData.file_url) {
      setMessage({ type: 'error', text: 'Te rog încarcă un fișier PDF!' });
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/guides/${editingId}`, formData);
        setMessage({ type: 'success', text: 'Ghid actualizat!' });
      } else {
        await axios.post(`${API_URL}/api/admin/guides`, formData);
        setMessage({ type: 'success', text: 'Ghid adăugat!' });
      }
      
      fetchGuides();
      resetForm();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea ghidului' });
    }
  };

  const handleEdit = (guide) => {
    setFormData({
      title: guide.title,
      description: guide.description || '',
      file_url: guide.file_url,
      cover_image: guide.cover_image || '',
      published: guide.published
    });
    setEditingId(guide.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ești sigură că vrei să ștergi acest ghid?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/guides/${id}`);
      setMessage({ type: 'success', text: 'Ghid șters!' });
      fetchGuides();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la ștergerea ghidului' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', file_url: '', cover_image: '', published: true });
    setEditingId(null);
    setShowForm(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Ghiduri PDF</h1>
          <p className="text-slate-400 mt-1">Încarcă și gestionează ghiduri pentru descărcare</p>
        </div>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Ghid
          </Button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-red-500/20 text-red-400 border border-red-500/50'
        }`}>
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl p-6 border border-slate-700 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-400" />
              {editingId ? 'Editează Ghid' : 'Ghid Nou'}
            </h2>
            <button type="button" onClick={resetForm} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* PDF Upload */}
          <div className="bg-slate-700/50 rounded-xl p-6 border-2 border-dashed border-slate-600">
            <div className="text-center">
              <Upload className="w-10 h-10 text-sky-400 mx-auto mb-3" />
              <p className="text-white font-medium mb-2">Încarcă fișier PDF</p>
              <p className="text-slate-400 text-sm mb-4">Maxim 50MB</p>
              
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer transition-colors ${
                  uploading 
                    ? 'bg-slate-600 text-slate-400' 
                    : 'bg-sky-500 hover:bg-sky-600 text-white'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Se încarcă... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Alege PDF
                  </>
                )}
              </label>

              {/* Progress bar */}
              {uploading && (
                <div className="mt-4 w-full bg-slate-600 rounded-full h-2">
                  <div 
                    className="bg-sky-500 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}

              {/* Show uploaded file */}
              {formData.file_url && (
                <div className="mt-4 p-3 bg-green-500/20 rounded-lg border border-green-500/50">
                  <p className="text-green-400 text-sm flex items-center gap-2 justify-center">
                    <FileText className="w-4 h-4" />
                    PDF încărcat cu succes!
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Titlu</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              placeholder="Ex: Ghid Începători LifeWave"
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Descriere (opțional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              placeholder="Scurtă descriere a ghidului..."
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">URL Imagine Copertă (opțional)</label>
            <input
              type="url"
              value={formData.cover_image}
              onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
              placeholder="https://example.com/cover.jpg"
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData({...formData, published: e.target.checked})}
              className="w-4 h-4 rounded bg-slate-700 border-slate-600"
            />
            <label htmlFor="published" className="text-sm text-slate-300">
              Publicat (vizibil pe site)
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl">
              <Save className="w-4 h-4 mr-2" />
              {editingId ? 'Salvează Modificările' : 'Adaugă Ghid'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">
              Anulează
            </Button>
          </div>
        </form>
      )}

      {/* Guides List */}
      <div className="space-y-4">
        {guides.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 text-center border border-slate-700">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Nu ai ghiduri încă</h3>
            <p className="text-slate-400">Încarcă primul tău ghid PDF pentru vizitatori!</p>
          </div>
        ) : (
          guides.map(guide => (
            <div 
              key={guide.id} 
              className="bg-slate-800 rounded-2xl p-4 border border-slate-700 flex items-center gap-4"
            >
              {/* Icon or Cover */}
              {guide.cover_image ? (
                <img 
                  src={guide.cover_image} 
                  alt={guide.title}
                  className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-8 h-8 text-red-400" />
                </div>
              )}
              
              {/* Content */}
              <div className="flex-grow min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{guide.title}</h3>
                    {guide.description && (
                      <p className="text-slate-400 text-sm mt-1 line-clamp-1">{guide.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-2">
                      <span>{formatDate(guide.created_at)}</span>
                      <span className={`flex items-center gap-1 ${guide.published ? 'text-green-400' : 'text-yellow-400'}`}>
                        {guide.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {guide.published ? 'Publicat' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <a
                      href={guide.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-green-400 hover:bg-slate-700 rounded-lg transition-colors"
                      title="Descarcă"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleEdit(guide)}
                      className="p-2 text-slate-400 hover:text-sky-400 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(guide.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminGuides;
